import OkC from "shared/util/OkcApi";

import {
	FIELDS,
	BINARY_GENDERS,
	NON_MONOGAMOUS,
	FIELDS_VALUE_MAP,
	WIW_API_PARAMS_MAP,
	MATCHSEARCH_API_MAP,
	GENDER_TAGS_PLURAL_MAP,
	RELATIONSHIP_STATUS_OPTIONS,
	MONOGAMY_OPTIONS,
} from "./constants";
import { GENTATION } from "shared/dictionaries/gentation";


// formatters
// ----------------------------------------------------------------------------

function _formatWiwParams(params) {
	const formattedParams = {};

	Object.keys(params).forEach((field) => {
		if (WIW_API_PARAMS_MAP[field]) {
			if (field === FIELDS_VALUE_MAP[FIELDS.DISTANCE]) {
				const isInteger = !!parseInt(params[field], 10);

				// 0 if the user choses `anywhere`
				const distanceValue = isInteger ? params[field] : 0;

				formattedParams[WIW_API_PARAMS_MAP[field]] = distanceValue;
			} else if (
				field === FIELDS_VALUE_MAP[FIELDS.GENDER] &&
				params[field].length === 1 && BINARY_GENDERS[params[field][0]]
			) {
				formattedParams.gender = params[field][0] === "man" ? "men" : "women";
			} else if (Array.isArray(params[field])) {
				formattedParams[WIW_API_PARAMS_MAP[field]] = [...params[field]];
			} else {
				formattedParams[WIW_API_PARAMS_MAP[field]] = params[field];
			}
		}
	});

	// singles only is ALWAYS false, no matter what dumb pub says
	formattedParams.singles_only = false;

	// monogamy should never exist as a top-level item
	delete formattedParams.monogamous;

	return formattedParams;
}

function _formatMatchSearchParams(params) {
	const formattedParams = {};

	Object.keys(params).forEach((field) => {
		if (MATCHSEARCH_API_MAP[field]) {
			// convert field to match search field
			formattedParams[MATCHSEARCH_API_MAP[field]] = params[field];
		} else {
			formattedParams[field] = params[field];
		}
	});

	// wiw location
	if (formattedParams.distance) {
		// if distance is a number, set radius to that number
		// else set to everywhere
		if (parseInt(formattedParams.distance, 10)) {
			formattedParams.locid = 0;
			formattedParams.radius = formattedParams.distance;
			formattedParams.located_anywhere = 0;
		} else {
			formattedParams.locid = null;
			formattedParams.radius = null;
			formattedParams.located_anywhere = 1;
		}
	}

	// filter out &mdash and non monogamous value for match search
	if (formattedParams.looking_for && formattedParams.looking_for.length) {
		formattedParams.looking_for = formattedParams.looking_for.filter((relationship) => {
			return relationship !== "&mdash;" && relationship !== NON_MONOGAMOUS;
		});
	}

	// new wiw takes in a list of genders the user is interested in
	// and the search_args endpoint takes gender tags in plural form.
	if (formattedParams.genders) {
		const genderTags = [];

		if (formattedParams.genders.length === Object.keys(GENDER_TAGS_PLURAL_MAP).length) {
			// 0 for all genders
			genderTags.push(0);
		} else {
			formattedParams.genders.forEach((gender) => {
				genderTags.push(GENDER_TAGS_PLURAL_MAP[gender]);
			});
		}

		formattedParams.gender_tags = genderTags;
	}

	// only send over orientation if they are straight only
	if (params.orientation === GENTATION.ORIENTATION_ALIASES.straight && params.isStraightOnly) {
		formattedParams.orientation_tags = params.orientation;
	} else {
		formattedParams.orientation_tags = 0;
	}

	// if we are in onboarding (as indicated by having a config name), we don't
	// need to "merge" the search args, we can blow away whatever they have saved
	// by default.
	if (
		formattedParams.configName !== "ONBOARDING_FUNNEL_WITH_PHONE" &&
		formattedParams.configName !== "ONBOARDING_FUNNEL"
	) {
		formattedParams.merge = true;
	}

	// delete unused params
	delete formattedParams.distance;
	delete formattedParams.isMetric;
	delete formattedParams.genders;
	delete formattedParams.orientation;
	delete formattedParams.isStraightOnly;
	delete formattedParams.userGender;
	delete formattedParams.configName;
	delete formattedParams.allow_bi_inference;

	// delete gentation; this works better if we only set desired genders and let
	// the backend handle gentation
	delete formattedParams.gentation;

	// make sure not to set monogamy or availability; this is more of a product/
	// matching decision
	delete formattedParams.availability;
	delete formattedParams.monogamy;

	return formattedParams;
}


// api calls
// ----------------------------------------------------------------------------

export function fetchAPIFields(isNonMonogamyOnboarding = false) {
	const getWiwParams = OkC.get("/profile/wiw_parameters", {
		api: 1,
		data: {
			get_nonmonogamy_fields: isNonMonogamyOnboarding,
		},
	});
	const getOrientationFilter = OkC.get("/profile/me/orientation_filter", { api: 1 });

	const calls = [getWiwParams, getOrientationFilter];

	return Promise.all(calls)
		.then((result) => {
			return Promise.resolve(result);
		})
		.catch(() => {
			return false;
		});
}

function saveWiw(data) {
	return OkC.put("/profile/me/wiw", {
		api: 1,
		data: _formatWiwParams(data),
	}).then(() => {
		// we don't actually care about the result of these calls, and we want to
		// use the data passed to this function in the next one, so return is here.
		return data;
	});
}

function saveMetric(data) {
	return OkC.post("/settings/metric", {
		api: 1,
		data: {
			metric: data.isMetric,
		},
	}).then(() => {
		// we don't actually care about the result of these calls, and we want to
		// use the data passed to this function in the next one, so return is here.
		return data;
	});
}

function saveMatchSettings(data) {
	return OkC.post("/match/search_args", {
		api: 1,
		data: _formatMatchSearchParams(data.data),
	}).then(() => {
		// we don't actually care about the result of these calls, and we want to
		// use the data passed to this function in the next one, so return is here.
		return data;
	});
}

function saveGenders(data) {
	return OkC.put("profile/me/wiw/gender", {
		api: 1,
		data: {
			genders: data.genders,
			allow_bi_inference: data.allow_bi_inference,
		},
	}).then(() => {
		// we don't actually care about the result of this call, and we want to
		// use the data passed to this function in the next one, so return is here.
		return data;
	});
}

function saveOrientationFilter(params) {
	const { isStraightOnly } = params;
	let data;
	let userGender;

	if (
		params.userGender &&
		GENTATION.GENDER_MAPPINGS.full[params.userGender]
	) {
		userGender = GENTATION.GENDER_MAPPINGS.full[params.userGender].toLowerCase();
	}

	if (isStraightOnly) {
		data = {
			show_straight: 1,
			show_gay: 0,
			show_bi: 0,
		};

		// if there's one gender, and it's your gender, you're gay
	} else if (params.genders.length === 1 && params.genders[0] === userGender) {
		data = {
			show_straight: 0,
			show_gay: 1,
			show_bi: 1,
		};
	} else {
		data = {
			show_straight: 1,
			show_gay: 1,
			show_bi: 1,
		};
	}

	return OkC.put("/profile/me/orientation_filter", {
		api: 1,
		data,
	}).then(() => {
		// we don't actually care about the result of this call, and we want to
		// use the data passed to this function in the next one, so return is here.
		return params;
	});
}

function getWiwSettings(data) {
	return OkC.get("/profile/wiw_parameters").then((res) => {
		// we need the results of this call, to be resolved by the long dumb chain
		// of promises, but also the data passed to this function, to pass along to
		// the match search settings function.
		return {
			data,
			wiwRes: res,
		};
	});
}

export function isIndianUser() {
	return OkC.get("/profile/me/is_indian_user");
}

/**
 * Save What-I-Want-related profile details.
 * @param {Object} details - a dictionary of details
 * @param {number} [details.relationshipStatus] - the id of the user's kind of
 *   relationship (e.g., single, seeing someone)
 * @param {number} [details.monogamy] - the id of the user's monogamy status
 *   (e.g. monogamous, non-monogamous)
 * @return {Promise} - a promise which resolves to saved profile detail data
 */
export const saveProfileDetails = (details = {}) => {
	if (!Object.keys(details).length) {
		return Promise.reject("missing params passed to saveProfileDetails in web.js");
	}

	const data = {};
	const relationshipStatusMap = {
		[RELATIONSHIP_STATUS_OPTIONS.single]: "single",
		[RELATIONSHIP_STATUS_OPTIONS.partnered]: "seeing_someone",
		[RELATIONSHIP_STATUS_OPTIONS.married]: "married",
	};
	const monogamyMap = {
		[MONOGAMY_OPTIONS.monogamous]: "monogamous",
		[MONOGAMY_OPTIONS.nonMonogamous]: "non_monogamous",
		[MONOGAMY_OPTIONS.openToEither]: "open_to_either",
	};

	if (details.relationshipStatus) {
		const status = relationshipStatusMap[details.relationshipStatus];

		if (status) {
			data.status = status;
		} else {
			return Promise.reject("invalid relationship status option passed to saveRelationshipStatus in web.js");
		}
	}

	if (details.monogamy) {
		const monogamous = monogamyMap[details.monogamy];

		if (monogamous) {
			data.monogamous = monogamous;
		} else {
			return Promise.reject("invalid monogamy option passed to saveMonogamy in web.js");
		}
	}

	return OkC.put("/profile/me", { data });
};

export function saveData(data) {
	// Due to the way the /profile/me/wiw endpoint saves data, you are required
	// to give it a gender, but it might apply the gender incorrectly. We
	// actually  want to set the gender with the /profile/me/wiw/gender endpoint.
	// So, here's what we're gonna do to make sure everything gets set correctly
	// in both the back-end and the interface:
	//
	// 1) POST to the batch /profile/me/wiw endpoint. this sets most things, but
	//    sets gender incorrectly.
	// 2) POST to most others together. this overwrites the messed up gender
	//    from the previous step.
	// 3) POST to the orientation filter; this has to go after setting gender
	// 4) GET `/profile/wiw_parameters` to re-set the wiw sentence on profile
	//
	// There's always a twist, though: mini onboarding avoids some of these
	// calls. So while that's running, we have two different code paths:
	//
	// In mini onboarding:
	// 1: save wiw + match
	//
	// Anywhere else:
	// 1: save wiw
	// 2: save gender
	// 3: save orientation filters
	// 4: save match
	// 5: get wiw


	// if a user has the default monogamy settings, they weren't saved in the
	// non-monogamy screens (since those screens were bypassed for the normy).
	// so fire a save for 'em. but don't bother putting it in the promise.all
	// below; it doesn't matter if this call fails (the backend will migrate
	// them later).
	if (
		window.location && window.location.href.indexOf("onboarding") >= 0
		&&
		(
			(
				data.monogamy === MONOGAMY_OPTIONS.monogamous
				&& data.relationshipStatus === RELATIONSHIP_STATUS_OPTIONS.single
			)
			|| !data.monogamy
			|| !data.relationshipStatus
			|| data.monogamy === "0"
			|| data.relationshipStatus === "0"
		)
	) {
		saveProfileDetails({
			monogamy: MONOGAMY_OPTIONS.monogamous,
			relationshipStatus: RELATIONSHIP_STATUS_OPTIONS.single,
		});
	}

	if (window.App && window.App.showLoader) {
		window.App.showLoader("saving what i want changes");
	}

	return saveWiw(data)
		.then(saveGenders)
		.then((resp) => {
			return Promise.all([
				saveOrientationFilter(resp),
				saveMetric(resp),
			]);
		})
		.then((resp) => {
			return Promise.all([
				getWiwSettings(resp),
				saveMatchSettings({ data: resp }),
			]);
		})
		.then(([{ wiwRes }]) => {
			if (
				window.App &&
				window.App.dataEvent &&
				typeof window.App.dataEvent === "function"
			) {
				window.App.dataEvent("userDetailsChangedEvent");

				if (window.App.hideLoader) {
					window.App.hideLoader("saving what i want changes success");
				}
			}

			return Promise.resolve(wiwRes);
		})
		.catch((err) => {
			console.log("err", err);
			return false;
		});
}



// WEBPACK FOOTER //
// ./src/apps/what_i_want/util/web.js